import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import MobileNavIcon from "../../../../../assets/icons/navicon";
import navBoxStyles from "./NavigationBox.m.css";

const decodeHtmlEntities = (text) => {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  return textarea.value;
};

const useNavigation = (story) => {
  const [navigationItems, setNavigationItems] = useState([]);
  const [hasAdImage, setHasAdImage] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const storyAttributes = get(story, ["metadata", "story-attributes"], {});
      const hasNavigation = get(storyAttributes, ["navigation", 0], "true") === "true";
      const storyTemplate = get(story, ["story-template"], "");
      const isLeadershipFocus = storyTemplate === "leadership-in-focus";

      const navItems = [];

      const cards = get(story, ["cards"], []);
      const hasAdImageCard = cards.some(card => {
        const cardAttributes = get(card, ["metadata", "attributes"], {});
        return get(cardAttributes, ["adimage", 0], "") === "true";
      });
      setHasAdImage(hasAdImageCard);

      if (hasNavigation) {
        cards.forEach((card) => {
          const storyElements = get(card, ["story-elements"], []);
          storyElements.forEach((element) => {
            const elementType = element.subtype || element.type || "";
            if (isLeadershipFocus && ["question", "answer", "q-and-a", "blockquote"].includes(elementType)) {
              return;
            }

            if (element.type === "text") {
              const text = element.text || "";

              const h2Regex = /<h2[^>]*>(.*?)<\/h2>/gi;
              const h3Regex = /<h3[^>]*>(.*?)<\/h3>/gi;

              let h2Match;
              while ((h2Match = h2Regex.exec(text)) !== null) {
                const rawContent = h2Match[1].replace(/<[^>]*>/g, "").trim();
                const content = decodeHtmlEntities(rawContent);
                const id = content.toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "");
                navItems.push({
                  id,
                  title: content,
                  level: 2,
                  children: [],
                });
              }

              if (navItems.length > 0) {
                const lastH2 = navItems[navItems.length - 1];
                let h3Match;
                h3Regex.lastIndex = 0;
                while ((h3Match = h3Regex.exec(text)) !== null) {
                  const rawContent = h3Match[1].replace(/<[^>]*>/g, "").trim();
                  const content = decodeHtmlEntities(rawContent);
                  const id = content.toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "");
                  lastH2.children.push({
                    id,
                    title: content,
                    level: 3,
                  });
                }
              }
            }
          });
        });
      }

      setNavigationItems(navItems);
    }
  }, [story]);

  return { navigationItems, hasAdImage };
};

const DesktopNavigation = ({ items, activeSection, setActiveSection, hasAdImage, expandedSections, setExpandedSections }) => {
  const navRef = useRef(null);

  const toggleSection = (id) => {
    setExpandedSections((prev) => {
      const isCurrentlyExpanded = prev[id];
      // Accordion: replace state instead of spreading to close others
      return { [id]: !isCurrentlyExpanded };
    });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 70;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const nav = navRef.current;
      const footer = document.querySelector("footer");

      if (!nav || !footer) return;

      if (window.innerWidth < 1280) {
        nav.style.transform = "translateY(0)";
        return;
      }

      const navHeight = nav.offsetHeight;
      const computedStyle = window.getComputedStyle(nav);
      const topOffset = parseInt(computedStyle.top, 10) || 150;
      const footerTop = footer.getBoundingClientRect().top;

      if (footerTop <= navHeight + topOffset) {
        const offset = navHeight + topOffset - footerTop;
        nav.style.transform = `translateY(-${offset}px)`;
      } else {
        nav.style.transform = "translateY(0)";
      }
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  return (
    <div
      ref={navRef}
      className={`${navBoxStyles["navigation-box"]} ${hasAdImage ? navBoxStyles["navigation-box-fixed"] : navBoxStyles["navigation-box-auto"]}`}
    >
      <div className={navBoxStyles["navigation-content"]}>
        {items.filter(item => item && item.id).map((item) => (
          <div key={item.id} className={navBoxStyles["nav-item"]}>
            <div className={navBoxStyles["nav-h2-wrapper"]}>
              <button
                className={`${navBoxStyles["nav-h2"]} ${activeSection === item.id ||
                  item.children?.some((child) => child.id === activeSection) ||
                  expandedSections[item.id]
                  ? navBoxStyles.active
                  : ""
                  }`}
                onClick={() => {
                  setActiveSection(item.id);
                  scrollToSection(item.id);
                }}
              >
                {item.title}
              </button>
              {item.children.length > 0 && (
                <button
                  className={navBoxStyles["expand-button"]}
                  onClick={() => toggleSection(item.id)}
                  aria-label={expandedSections[item.id] ? "Collapse" : "Expand"}
                >
                  {expandedSections[item.id] ? "−" : "+"}
                </button>
              )}
            </div>
            {item.children.length > 0 && expandedSections[item.id] && (
              <div className={navBoxStyles["nav-children"]}>
                {item.children.map((child) => (
                  <button
                    key={child.id}
                    className={`${navBoxStyles["nav-h3"]} ${activeSection === child.id ? navBoxStyles.active : ""
                      }`}
                    onClick={() => {
                      scrollToSection(child.id);
                      setActiveSection(child.id);
                    }}
                  >
                    {child.title}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

DesktopNavigation.propTypes = {
  items: PropTypes.array.isRequired,
  activeSection: PropTypes.string,
  setActiveSection: PropTypes.func.isRequired,
  hasAdImage: PropTypes.bool.isRequired,
};

const MobileNavButton = ({ onClick }) => (
  <button
    className={navBoxStyles["mobile-nav-button"]}
    onClick={onClick}
    aria-label="Toggle navigation"
  >
    <MobileNavIcon />
  </button>
);

MobileNavButton.propTypes = {
  onClick: PropTypes.func.isRequired,
};

const MobileNavModal = ({ items, activeSection, setActiveSection, onClose }) => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (id) => {
    setExpandedSections((prev) => {
      const isCurrentlyExpanded = prev[id];
      return { [id]: !isCurrentlyExpanded };
    });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 70;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
    onClose();
  };

  return (
    <>
      <div className={navBoxStyles["mobile-nav-overlay"]} onClick={onClose}></div>
      <div className={navBoxStyles["mobile-navigation-box"]}>
        <div className={navBoxStyles["navigation-content"]}>
          {items.filter(item => item && item.id).map((item) => (
            <div key={item.id} className={navBoxStyles["nav-item"]}>
              <div className={navBoxStyles["nav-h2-wrapper"]}>
                <button
                  className={`${navBoxStyles["nav-h2"]} ${activeSection === item.id ||
                    item.children?.some((child) => child.id === activeSection) ||
                    expandedSections[item.id]
                    ? navBoxStyles.active
                    : ""
                    }`}
                  onClick={() => {
                    setActiveSection(item.id);
                    scrollToSection(item.id);
                  }}
                >
                  {item.title}
                </button>
                {item.children.length > 0 && (
                  <button
                    className={navBoxStyles["expand-button"]}
                    onClick={() => toggleSection(item.id)}
                    aria-label={expandedSections[item.id] ? "Collapse" : "Expand"}
                  >
                    {expandedSections[item.id] ? "−" : "+"}
                  </button>
                )}
              </div>
              {item.children.length > 0 && expandedSections[item.id] && (
                <div className={navBoxStyles["nav-children"]}>
                  {item.children.map((child) => (
                    <button
                      key={child.id}
                      className={`${navBoxStyles["nav-h3"]} ${activeSection === child.id ? navBoxStyles.active : ""
                        }`}
                      onClick={() => {
                        scrollToSection(child.id);
                        setActiveSection(child.id);
                      }}
                    >
                      {child.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

MobileNavModal.propTypes = {
  items: PropTypes.array.isRequired,
  activeSection: PropTypes.string,
  setActiveSection: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

const NavigationBox = ({ story }) => {
  const { navigationItems, hasAdImage } = useNavigation(story);
  const [activeSection, setActiveSection] = useState(null);
  const [isMobileNavOpen, setMobileNavOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    if (!navigationItems.length) return;

    // Accordion: Expand ONLY the section that is active or contains the active child
    navigationItems.forEach(item => {
      const isChildActive = item.children?.some(child => child.id === activeSection);
      if (item.id === activeSection || isChildActive) {
        setExpandedSections({ [item.id]: true });
      }
    });
  }, [activeSection, navigationItems]);

  useEffect(() => {
    if (!navigationItems.length) return;

    const observerOptions = {
      root: null,
      rootMargin: "-20% 0px -70% 0px", // Trigger when heading is in the top portion
      threshold: 0
    };

    const handleIntersect = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    // Get all heading IDs to observe
    const idsToObserve = [];
    navigationItems.forEach(item => {
      if (item.id) idsToObserve.push(item.id);
      item.children.forEach(child => {
        if (child.id) idsToObserve.push(child.id);
      });
    });

    idsToObserve.forEach(id => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [navigationItems]);

  if (!navigationItems.length) return null;

  return (
    <div className="hide-element">
      <DesktopNavigation
        items={navigationItems}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        hasAdImage={hasAdImage}
        expandedSections={expandedSections}
        setExpandedSections={setExpandedSections}
      />
      <MobileNavButton onClick={() => setMobileNavOpen(true)} />
      {isMobileNavOpen && (
        <MobileNavModal
          items={navigationItems}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          onClose={() => setMobileNavOpen(false)}
        />
      )}
    </div>
  );
};

NavigationBox.propTypes = {
  story: PropTypes.object.isRequired,
};

export default NavigationBox;
